import React from 'react';
import { KPIMetrics } from '../../types';
import { getActualColor, getBillableRateColor } from '../../utils/colorHelpers';

export type KPICardId =
  | 'budget-total-month'
  | 'budget-as-of-yesterday'
  | 'actual-as-of-yesterday'
  | 'budget-billable-rate'
  | 'billable-rate-so-far';

interface KPICardWidgetProps {
  cardId: KPICardId;
  kpiMetrics: KPIMetrics;
  isLoading: boolean;
}

const LoadingCard = () => (
  <div className="flex flex-col items-center justify-center space-y-2 py-4">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
    <p className="text-xs text-slate-600">Loading...</p>
  </div>
);

interface CardConfig {
  title: string;
  iconBg: string;
  iconColor: string;
  iconPath: string;
  getValue: (m: KPIMetrics) => string;
  getColorClass: (m: KPIMetrics) => string;
}

const CARD_CONFIGS: Record<KPICardId, CardConfig> = {
  'budget-total-month': {
    title: 'Budget total month',
    iconBg: 'from-slate-100 to-slate-200',
    iconColor: 'text-slate-600',
    iconPath: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1',
    getValue: (m) => m.budgetTotalMonth != null ? `${Math.round(m.budgetTotalMonth).toLocaleString('no-NO')} kr` : 'N/A',
    getColorClass: () => 'text-slate-900',
  },
  'budget-as-of-yesterday': {
    title: 'Budget as of yesterday',
    iconBg: 'from-slate-100 to-slate-200',
    iconColor: 'text-slate-600',
    iconPath: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
    getValue: (m) => m.budgetAsOfYesterday != null ? `${Math.round(m.budgetAsOfYesterday).toLocaleString('no-NO')} kr` : 'N/A',
    getColorClass: () => 'text-slate-900',
  },
  'actual-as-of-yesterday': {
    title: 'Actual as of yesterday',
    iconBg: 'from-green-100 to-green-200',
    iconColor: 'text-green-600',
    iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    getValue: (m) => m.actualAsOfYesterday != null ? `${Math.round(m.actualAsOfYesterday).toLocaleString('no-NO')} kr` : 'N/A',
    getColorClass: (m) => getActualColor(m.actualAsOfYesterday || 0, m.budgetAsOfYesterday || 0),
  },
  'budget-billable-rate': {
    title: 'Budget billable rate',
    iconBg: 'from-slate-100 to-slate-200',
    iconColor: 'text-slate-600',
    iconPath: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
    getValue: (m) => m.budgetBillableRate != null ? `${Math.round(m.budgetBillableRate)}%` : 'N/A',
    getColorClass: () => 'text-slate-900',
  },
  'billable-rate-so-far': {
    title: 'Billable rate so far',
    iconBg: 'from-blue-100 to-blue-200',
    iconColor: 'text-blue-600',
    iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    getValue: (m) => m.billableRateSoFar != null ? `${Math.round(m.billableRateSoFar * 100)}%` : 'N/A',
    getColorClass: (m) => getBillableRateColor(m.billableRateSoFar || 0, m.budgetBillableRate || 0),
  },
};

export const KPICardWidget: React.FC<KPICardWidgetProps> = ({ cardId, kpiMetrics, isLoading }) => {
  const config = CARD_CONFIGS[cardId];
  if (!config) return <div>Unknown card: {cardId}</div>;

  return (
    <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl shadow-md border border-slate-200/60 p-4 hover:shadow-lg transition-all duration-300">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-700">{config.title}</h3>
        <div className={`w-8 h-8 bg-gradient-to-br ${config.iconBg} rounded-lg flex items-center justify-center`}>
          <svg className={`w-4 h-4 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config.iconPath} />
          </svg>
        </div>
      </div>
      {isLoading ? <LoadingCard /> : (
        <p className={`font-bold whitespace-nowrap ${config.getColorClass(kpiMetrics)}`} style={{ fontSize: '22px' }}>
          {config.getValue(kpiMetrics)}
        </p>
      )}
    </div>
  );
};

export default KPICardWidget;
